import React, { useState, useEffect } from 'react'
import { s3Static } from '../../../../utils/constants'
import Image from 'next/image'
import Link from 'next/link'
import { AnimatePresence } from 'framer-motion'
import { Services } from '../../service/Services'
import { RequestProvider } from '../../../../context/RequestContext'
import { useRouter } from 'next/router'
import { Faq } from '../../../commons/Faq'
import { handymanSingaporeFaqs } from '../../../../utils/faq'
import { openModal } from '../../../../service/modal-service'
import { HandymanBooking } from './HandymanBooking'
import { WhyUs } from '@/commons/service-hub/WhyUs'
import { ReviewsSection } from '@/commons/service-hub/ReviewsSection'
import { RewardsSection } from '@/commons/service-hub/RewardsSection'
import { technicianReasons } from 'data/service-hub-content'
import { HandymanDealItems } from './HandymanDealItems'
import { PriceList } from '@/commons/service-hub/PriceList'
import { HandymanServices } from './HandymanServices'
import { HowToBook } from '@/commons/service-hub/HowToBook'
import { StickyCta } from '@/commons/service-hub/StickyCta'
import { AreasServed, RelatedLinks, technicianAreasIntro } from '@/commons/service-hub/RelatedLinks'
import { googleRating, googleReviewCount } from 'utils/business-info'
import { findItems, formatPrice, fromPrice } from '../../../../utils/handyman-price-data'
import {
    ELECTRICIAN_HUB,
    ELECTRICIAN_PRICE_PAGE,
    HANDYMAN_HUB,
    HANDYMAN_PRICE_PAGE,
    PLUMBER_HUB,
    handymanJobPages,
    jobPagePath,
    jobPagesForHub,
} from '../../../../data/handyman-job-pages'

// The "popular prices" teaser, one or two per category. The full list lives on
// the price page, which owns "handyman singapore price list".
const POPULAR_ITEMS = [
    'Replace light bulb',
    'Install ceiling fan',
    'Fix water leak from faucet, sink or pipes',
    'Clear clogged toilet bowl',
    'Door handle/knob replacement',
    'Door/Gate lock replacement',
    'Hang Mirror',
    'Installing Curtain Rods',
    'Install TV bracket for wall mount',
    'Fix/install bedframe',
    'Painting service for 1 room',
]

const relatedLinks = [
    ...jobPagesForHub(HANDYMAN_HUB).map((slug) => ({ href: jobPagePath(slug), label: handymanJobPages[slug].breadcrumbTitle })),
    { href: HANDYMAN_PRICE_PAGE, label: 'Handyman Price List', desc: 'Every job and price in one place' },
    { href: ELECTRICIAN_HUB, label: 'Electrician Services', desc: 'Lighting, power points and wiring' },
    { href: ELECTRICIAN_PRICE_PAGE, label: 'Electrician Price List', desc: 'Electrical jobs and prices' },
    { href: PLUMBER_HUB, label: 'Plumber Services', desc: 'Leaks, taps, toilets and heaters' },
    { href: jobPagePath('clogged-toilet-bowl'), label: 'Clogged Toilet Bowl', desc: 'Guide' },
    { href: jobPagePath('leaking-faucet-sink-pipe-repair'), label: 'Faucet, Sink & Pipe Leaks', desc: 'Guide' },
    { href: `${HANDYMAN_HUB}/top-10-maintenance-tasks-by-handyman-plumber-electrician`, label: 'Top 10 Home Maintenance Tasks', desc: 'Guide' },
    { href: `${HANDYMAN_HUB}/install-water-heater-tv-bracket-ceiling-fan-at-affordable-pricing`, label: 'Water Heater, TV Bracket & Fan Installation', desc: 'Guide' },
]

export const Handyman = (props) => {
    const requestCategorySettings = props.requestCategorySettings;
    const countryName = props.countryName
    const testimonials = props.testimonials
    const handymanType = props.handymanType
    const handymanPriceList = props.handymanPriceList
    const requestType = 'Handyman Services'

    const router = useRouter()

    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isShowDealItems, setIsShowDealItems ] = useState(false)
    const [ requestForm, setRequestForm ] = useState(false)

    // Handyman bookings always start from the priced job picker, never the
    // question wizard (which lacks most jobs and their details). A
    // ?requestForm=true link opens the picker.
    useEffect(() => {
        if (router.query.requestForm) setIsShowDealItems(true)
    }, [router.query.requestForm])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const popularItems = findItems(handymanPriceList, POPULAR_ITEMS, 'handyman hub')
    const lowest = fromPrice(handymanPriceList)

  return (
    <>
        <RequestProvider>
            <section id="handyman-singapore" className='relative'>
                {/* Static image on every screen size. Mobile used to autoplay a
                    2.4MB video here, which was the page's LCP element. */}
                <div className="-mt-14 relative w-full aspect-21/9 sm:aspect-auto sm:h-200 overflow-hidden">
                    <Image
                        src={s3Static + "handyman/chinese-handyman-with-hammer.jpg"}
                        fill
                        className="object-cover"
                        alt="Handyman with tools ready for a home repair in Singapore"
                        priority
                        sizes="100vw"
                    />
                </div>
                <div id="book" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2 scroll-mt-20'>
                    <HandymanBooking requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} handymanType={handymanType}
                        setIsQuickBook={setIsQuickBook} countryName={countryName} setIsShowDealItems={setIsShowDealItems}
                        handymanPriceList={handymanPriceList}
                        title="Handyman Singapore: Same-Day Home Repairs at Fixed Prices"
                        subtitle={`Plumbing, electrical, mounting, locks, furniture and painting${lowest != null ? ` from ${formatPrice(lowest)}` : ''}. Handymen island-wide, 8am to 10pm daily.`}/>
                </div>
            </section>
            {isShowDealItems &&
                <AnimatePresence>
                    <HandymanDealItems requestCategorySettings={requestCategorySettings} handymanPriceList={handymanPriceList} setRequestForm={setRequestForm} setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} setIsShowDealItems={setIsShowDealItems}/>
                </AnimatePresence>
            }
            {requestForm &&
                <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} asSheet/>
            }
        </RequestProvider>

        <PriceList
            items={popularItems}
            title="Popular Handyman Prices in Singapore"
            subtitle="Fixed prices per job. You see the total before you confirm, with no hourly billing."
            moreHref={HANDYMAN_PRICE_PAGE}
            moreLabel="See the full handyman price list"
        />

        <section className='max-w-6xl mx-auto py-8 px-2'>
            <div className='grid md:grid-cols-3 gap-5 items-center'>
                <header className='md:col-span-2 space-y-5 px-2'>
                    <h2 className='text-2xl md:text-3xl text-center'>
                        <strong>Trusted Handyman Services in Singapore</strong>
                    </h2>
                    <div className='space-y-4 text-sm md:text-base leading-relaxed text-gray-700'>
                        <p>
                            <strong>StringsSG</strong> has connected Singapore homes with handymen since 2018 for home repairs and
                            maintenance: plumbing and electrical work, door locks and handles, TV and wall mounting, curtain rails,
                            furniture assembly and painting.
                        </p>
                        <ul className='list-disc pl-5 space-y-2'>
                            <li><strong>Fixed prices:</strong> every job has a published price, shown before you confirm</li>
                            <li><strong>Rated {googleRating} on Google:</strong> from {googleReviewCount} customer reviews</li>
                            <li><strong>Same-day slots:</strong> technicians attend from 8am to 10pm daily, including weekends</li>
                            <li><strong>Backed by a guarantee:</strong> 30-day workmanship warranty and a 100% satisfaction guarantee</li>
                        </ul>
                        <p>
                            Every job follows the service steps and quality standards StringsSG defines, and we take responsibility for its quality.{' '}
                            <Link href="/sg/handyman-singapore-services/how-stringssg-works" prefetch={false} className='text-orange-600 hover:underline'>
                                How StringsSG handyman services work
                            </Link>
                        </p>
                    </div>
                </header>
                <div className='md:col-span-1 relative w-full h-100 rounded-xl hidden md:block'>
                    <Image className='rounded-xl opacity-90'
                        src={s3Static + "handyman-png.png"}
                        fill
                        style={{objectFit: 'contain', objectPosition: 'center'}}
                        placeholder='blur'
                        blurDataURL={s3Static + "placeholder.png"}
                        alt='Handyman Singapore - StringsSG'
                        sizes="33vw"
                    />
                </div>
            </div>
        </section>

        <section className='bg-gray-100 py-10 px-2'>
            <h2 className='text-2xl md:text-3xl uppercase font-bold text-center mb-4'>
                Our Handyman Services in Singapore
            </h2>
            <HandymanServices/>
        </section>

        <HowToBook serviceLabel="handyman"/>
        <WhyUs title="Why Choose StringsSG Handyman Services?" reasons={technicianReasons('Handyman')}/>

        <ReviewsSection title="What Customers Say About Our Handymen" testimonials={testimonials} />

        <AreasServed title="Handyman Near You, Island-Wide" intro={technicianAreasIntro('handyman')}/>

        <RewardsSection serviceNoun="handyman" />

        <section className='max-w-5xl mx-auto py-10'>
            <Faq title="Handyman Service FAQs" questionsAnswers={handymanSingaporeFaqs}/>
        </section>

        <RelatedLinks title="Handyman Jobs, Prices and Guides" links={relatedLinks}/>
        {!requestForm && !isShowDealItems &&
            <StickyCta onBook={() => setIsShowDealItems(true)} whatsAppText="Hi, I'd like to book a handyman."/>
        }
    </>
  )
}
